import React from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import CustomTooltip from "../../Common/CustomTooltip";
import zoomPlugin from "chartjs-plugin-zoom";
import { mapDataToLabels } from "../../../utils/helper";

ChartJS.register(...registerables, zoomPlugin);

const AreaChartWithCustomTooltip = (props) => {
  const {
    data: chartDataProps,
    valueProperty = "value",
    labels,
    hoveredIndex,
    graph1Ref,
    selectedOption,
    settingsData,
  } = props;
  // Extract data
  const [
    dataMain,
    dataFBM,
    dataAmazon,
    dataBsr,
    dataBb,
    dataOffers,
    dataRatings,
    dataReviews,
  ] = chartDataProps || [];

  const data_L = mapDataToLabels(
    dataMain,
    labels,
    selectedOption,
    valueProperty
  );
  const dataFBM_L = mapDataToLabels(
    dataFBM,
    labels,
    selectedOption,
    valueProperty
  );
  const dataAmazon_L = mapDataToLabels(
    dataAmazon,
    labels,
    selectedOption,
    valueProperty
  );
  const dataBsr_L = mapDataToLabels(
    dataBsr,
    labels,
    selectedOption,
    valueProperty
  );
  const dataBb_L = mapDataToLabels(
    dataBb,
    labels,
    selectedOption,
    valueProperty
  );

  // Fill in missing values by carrying forward the last known value
  const fillMissingValues = (data, skipZeros = false) => {
    const filledData = [];
    let lastValue = null;

    data.forEach((value) => {
      if (value !== null && (!skipZeros || value > 0)) {
        lastValue = value;
      }
      filledData.push(lastValue);
    });

    return filledData;
  };

  const filledData = fillMissingValues(data_L, true);
  const filledDataFBM = fillMissingValues(dataFBM_L, true);
  const filledDataAmazon = fillMissingValues(dataAmazon_L);
  // const filledDataBsr = fillMissingValues(dataBsr_L);
  const filledDataBb = fillMissingValues(dataBb_L);

  const min = Math.min(
    ...filledData,
    ...filledDataFBM,
    ...filledDataAmazon,
    // ...filledDataBsr,
    ...filledDataBb
  );
  const max = Math.max(
    ...filledData,
    ...filledDataFBM,
    ...filledDataAmazon,
    // ...filledDataBsr,
    ...filledDataBb
  );

  const valueFormatter = (num) => {
    const parsed = parseFloat(num);
    if (parsed < 0 || isNaN(parsed)) {
      return "-";
    }
    return parsed.toFixed(2).toString();
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: "FBA",
        data: filledData,
        borderColor: "#f97316",
        borderWidth: 3,
        stepped: true,
        pointRadius: 0,
        order: 1
      },
      {
        label: "FBM",
        data: filledDataFBM,
        borderColor: "#0ea5e9",
        borderWidth: 3,
        stepped: true,
        pointRadius: 0,
        order: 1
      },
      {
        label: "Amazon",
        data: filledDataAmazon,
        borderColor: "#ba900b",
        backgroundColor: "rgba(166, 128,10, 0.4)",
        borderWidth: 3,
        stepped: true,
        pointRadius: 0,
        fill: true,
        order: 1
      },
      {
        label: "Buy Box",
        data: filledDataBb,
        borderColor: "#ec4899",
        borderWidth: 3,
        stepped: true,
        pointRadius: 0,
        fill: false,
        order: 0
      },
    ],
  };

  const chartOptions = {
    isAreaChart: true,
    plugins: {
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgba(10,128,255,0.3)",
          },
          mode: "x",
          onZoom: ({ chart }) => {
            if (props.onZoom) {
              props.onZoom(chart);
            }
          },
          onZoomComplete: ({ chart }) => {
            if (props.onZoom) {
              props.onZoom(chart);
            }
          },
        },
        limits: {
          x: { min: "original", max: "original" },
        },
      },
      tooltip: {
        enabled: false, // Disable default tooltip
        // callbacks: {
        //   label: (context) => {
        //     const label = context.dataset.label || "";
        //     const value = context.raw || 0;
        //     return `${label}: ${valueFormatter(value)}`;
        //   },
        // },
      },
      legend: {
        display: false, // Disable legend to remove labels at the top
      },
      // corsair: {
      //     horizontal: false,
      //     vertical: true,
      //     color: 'red',
      //     dash: [],
      //     width: 2
      // }
    },
    onClick: (e, elements, chart) => {
      if (e.native.detail === 2) {
        if (props.onResetZoom) {
          props.onResetZoom();
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: "#E5E7EB", // gray-200
          font: {
            size: 9,
          },
          callback: function (value, index, ticks) {
            const fullLabel = this.getLabelForValue(value);
            const [day, month] = fullLabel.split(" ");
            return `${day} ${month}`;
          },
        },
        grid: {
          color: "rgba(229, 229, 229, 0.3)",
        },
        border: {
          color: "#E5E7EB", // gray-200
          width: 1,
        },
      },
      y: {
        suggestedMin: min + 25,
        suggestedMax: max,
        ticks: {
          // callback: valueFormatter,
          color: "#E5E7EB", // gray-200
        },
        grid: {
          color: "rgba(229, 229, 229, 0.3)",
        },
        border: {
          color: "#E5E7EB", // gray-200
          width: 1,
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div
      className="bg-secondary-bg flex flex-col border border-primary/100 rounded-lg shadow-sm w-full p-1 relative"
      style={{
        minWidth: "100%",
        minHeight: "150px",
        // minHeight: '200px'
      }}
    >
      <Line
        id="areachart"
        data={chartData}
        options={chartOptions}
        style={
          {
            // minWidth: '100%'
          }
        }
        ref={(chart) => {
          if (chart) {
            graph1Ref.current = chart.chartInstance || chart;
          }
        }}
      />
      <CustomTooltip
        chart={graph1Ref}
        valueFormatter={valueFormatter}
        chart_type={"prices"}
        settingsData={settingsData}
      />
    </div>
  );
};

export default AreaChartWithCustomTooltip;
